import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TranslateModule } from '@ngx-translate/core';

import { Globals } from '@core/globals';
import { ButtonComponent } from '@atoms/button/button.component';
import { CheckboxComponent } from '@atoms/checkbox/checkbox.component';
import { FormComponent } from '@atoms/form/form.component';
import { FormInputsComponent } from '@atoms/form/form-inputs/form-inputs.component';
import { FormSectionComponent } from '@atoms/form/form-section/form-section.component';
import { FormTitleComponent } from '@atoms/form/form-title/form-title.component';
import { GridComponent } from '@atoms/grid/grid.component';
import { InputComponent } from '@atoms/input/input.component';
import { LinkComponent } from '@atoms/link/link.component';
import { ParagraphComponent } from '@atoms/paragraph/paragraph.component';
import { PhoneInputComponent } from '@atoms/phone-input/phone-input.component';
import { SelectComponent } from '@atoms/select/select.component';
import { SelectItemComponent } from '@atoms/select/select-item/select-item.component';
import { TextareaComponent } from '@atoms/textarea/textarea.component';
import { ContactInformationsComponent } from '@molecules/contact-informations/contact-informations.component';
import { FormThanksMessageComponent } from '@molecules/form-thanks-message/form-thanks-message.component';
import { ModalHeaderComponent } from '@molecules/modal/modal-header/modal-header.component';
import { ModalMainComponent } from '@molecules/modal/modal-main/modal-main.component';
import { ModalMainContentComponent } from '@molecules/modal/modal-main-content/modal-main-content.component';
import { ModalSidebarComponent } from '@molecules/modal/modal-sidebar/modal-sidebar.component';
import { ModalTitleComponent } from '@molecules/modal/modal-title/modal-title.component';
import { GlobalsService } from '@services/globals.service';
import { ContactFormService } from '@services/contact-form.service';
import { GtmService } from '@services/gtm.service';

@Component({
  selector: 'app-book-appointment-modal',
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    ButtonComponent,
    CheckboxComponent,
    FormComponent,
    FormInputsComponent,
    FormSectionComponent,
    FormThanksMessageComponent,
    FormTitleComponent,
    GridComponent,
    InputComponent,
    LinkComponent,
    ParagraphComponent,
    PhoneInputComponent,
    SelectComponent,
    SelectItemComponent,
    TextareaComponent,
    ContactInformationsComponent,
    ModalHeaderComponent,
    ModalMainComponent,
    ModalMainContentComponent,
    ModalSidebarComponent,
    ModalTitleComponent,
  ],
  providers: [DatePipe],
  templateUrl: './book-appointment-modal.component.html',
  styleUrl: './book-appointment-modal.component.scss',
})
export class BookAppointmentModalComponent implements OnInit {
  private fb = inject(FormBuilder);
  private destroyRef = inject(DestroyRef);
  public globals = inject(Globals);
  private globalsService = inject(GlobalsService);
  private contactService = inject(ContactFormService);
  private gtmService = inject(GtmService);
  private datePipe = inject(DatePipe);
  public form = this.fb.nonNullable.group({
    name: ['', { nonNullable: true, validators: [Validators.required] }],
    email: ['', { nonNullable: true, validators: [Validators.required, Validators.email] }],
    phone: ['', { nonNullable: true, validators: [Validators.required] }],
    phoneCode: ['+420', [Validators.required]],
    message: ['', { nonNullable: true, validators: [] }],
    date: ['', { nonNullable: true, validators: [Validators.required] }],
    time: ['', { nonNullable: true, validators: [Validators.required] }],
    marketingAgree: [false, { nonNullable: true, validators: [] }],
  });
  public timeOptions: string[] = [];
  public step: number = 1;

  send(): void {
    if (this.form.invalid) return;
    const formData = this.form.getRawValue();
    const date = this.datePipe.transform(formData.date, 'yyyy-MM-dd');
    const [hours, minutes] = formData.time.split(':');
    const callBackFormData = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      message: formData.message,
      country: formData.phoneCode,
      date: date,
      hours: hours,
      minutes: minutes,
      marketingAgree: formData.marketingAgree,
      personalAgree: true,
    };
    this.contactService
      .sendCallBackForm(callBackFormData, 'contactUs')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.form.reset();
        this.form.markAsUntouched();
        this.step = 2;
        const dataLayer = {
          event: 'page_view',
          pageURL: '/' + this.globals.language() + '/podekovani-schuzka-formular',
          pageType: 'Purchase-8',
          pageTitle: 'Poděkování kontaktní formulář',
        };
        this.gtmService.pushGtm(dataLayer);
      });
  }

  ngOnInit(): void {
    this.timeOptions = this.globalsService.createTimeOptions();

    const dataLayer = {
      event: 'page_view',
      pageURL: '/' + this.globals.language() + '/schuzka-formular',
      pageType: 'Form-8',
      pageTitle: 'Kontaktní formulář',
    };
    this.gtmService.pushGtm(dataLayer);
  }
}
